import { useMutation, useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { v4 } from 'uuid';

import { QUERY_ROOT_ID, cacheUtils } from '@eluve/apollo-client';
import { useToast } from '@eluve/components';
import { ResultOf, graphql } from '@eluve/graphql.tada';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

const TenantUserSnippetFragment = graphql(`
  fragment TenantUserSnippet on TenantUserSnippets @_unmask {
    __typename
    id
    title
    content
  }
`);

const TennatUserSnippetsFragment = graphql(
  `
    fragment TenantUserSnippets on query_root @_unmask {
      tenantUserSnippets(orderBy: { createdAt: DESC }) {
        ...TenantUserSnippet
      }
    }
  `,
  [TenantUserSnippetFragment],
);

const getUserSnippets = graphql(
  `
    query getTenantUserSnippets {
      ...TenantUserSnippets
    }
  `,
  [TennatUserSnippetsFragment],
);

const addUserSnippet = graphql(
  `
    mutation InsertSnippet($title: String!, $content: String!) {
      insertTenantUserSnippetsOne(
        object: { title: $title, content: $content }
      ) {
        ...TenantUserSnippet
      }
    }
  `,
  [TenantUserSnippetFragment],
);

const updateUserSnippet = graphql(
  `
    mutation UpdateSnippet(
      $tenantId: uuid!
      $id: uuid!
      $title: String!
      $content: String!
    ) {
      updateTenantUserSnippetsByPk(
        pkColumns: { tenantId: $tenantId, id: $id }
        _set: { title: $title, content: $content }
      ) {
        ...TenantUserSnippet
      }
    }
  `,
  [TenantUserSnippetFragment],
);

const deleteUserSnippet = graphql(`
  mutation DeleteUserSnippet($tenantId: uuid!, $id: uuid!) {
    deleteTenantUserSnippetsByPk(tenantId: $tenantId, id: $id) {
      id
      __typename
    }
  }
`);

export type TenantUserSnippet = ResultOf<typeof TenantUserSnippetFragment>;

export const useSnippets = () => {
  const toast = useToast();
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();

  const analytics = useAnalytics('snippets');
  const location = useLocation();
  const isReviewerDash = location.pathname.includes('review/');

  const { data, loading } = useQuery(getUserSnippets, {
    skip: isReviewerDash || !tenantId || !userId,
  });

  const snippets = data?.tenantUserSnippets ?? [];

  const [addSnippetMutation] = useMutation(addUserSnippet, {
    onCompleted: (data) => {
      const newSnippet = data?.insertTenantUserSnippetsOne;
      analytics.capture('user_created_snippet', {
        tenantId,
        userId,
        snippetId: newSnippet?.id,
        title: newSnippet?.title,
        content: newSnippet?.content,
      });
    },
    optimisticResponse: ({ title, content }) => ({
      insertTenantUserSnippetsOne: {
        __typename: 'TenantUserSnippets' as const,
        id: `temp-${v4()}`,
        title,
        content,
      },
    }),
    update: (_cache, { data: mutationData }) => {
      cacheUtils.updateFragment(
        {
          fragment: TennatUserSnippetsFragment,
          key: { id: QUERY_ROOT_ID },
        },
        (existing) => {
          const newSnippet = mutationData?.insertTenantUserSnippetsOne;
          if (!existing || !newSnippet) {
            return existing;
          }
          return {
            tenantUserSnippets: [
              newSnippet,
              ...(existing.tenantUserSnippets || []),
            ],
          };
        },
      );
    },
    onError: () => {
      toast.error('Failed to create snippet');
    },
  });

  const [updateSnippetMutation] = useMutation(updateUserSnippet, {
    onCompleted: (data) => {
      const updatedSnippet = data?.updateTenantUserSnippetsByPk;
      analytics.capture('user_updated_snippet', {
        tenantId,
        userId,
        snippetId: updatedSnippet?.id,
        title: updatedSnippet?.title,
        content: updatedSnippet?.content,
      });
    },
    optimisticResponse: ({ id, title, content }) => ({
      updateTenantUserSnippetsByPk: {
        __typename: 'TenantUserSnippets' as const,
        id,
        title,
        content,
      },
    }),
    onError: () => {
      toast.error('Failed to update snippet');
    },
  });

  const [deleteSnippetMutation] = useMutation(deleteUserSnippet, {
    optimisticResponse: ({ id }) => ({
      deleteTenantUserSnippetsByPk: {
        __typename: 'TenantUserSnippets' as const,
        id,
      },
    }),
    update: (_cache, { data: mutationData }) => {
      cacheUtils.updateFragment(
        {
          fragment: TennatUserSnippetsFragment,
          key: { id: QUERY_ROOT_ID },
        },
        (existing) => {
          const deletedSnippet = mutationData?.deleteTenantUserSnippetsByPk;
          if (!existing || !deletedSnippet) {
            return existing;
          }
          return {
            tenantUserSnippets: existing.tenantUserSnippets.filter(
              (snippet) => snippet.id !== deletedSnippet.id,
            ),
          };
        },
      );
    },
    onError: () => {
      toast.error('Failed to delete snippet');
    },
  });

  const addSnippet = async (title: string, content: string) => {
    return await addSnippetMutation({
      variables: { title, content },
    });
  };

  const updateSnippet = async (id: string, title: string, content: string) => {
    return await updateSnippetMutation({
      variables: { id, tenantId, title, content },
    });
  };

  const deleteSnippet = async (id: string) => {
    return await deleteSnippetMutation({ variables: { id, tenantId } });
  };

  return {
    tenantId,
    userId,
    snippets,
    loading,
    addSnippet,
    updateSnippet,
    deleteSnippet,
  };
};
